import React, {ReactElement, useEffect} from 'react';
import {Button} from '@/shadcn_components/ui/button';
import {DropdownMenuLabel, DropdownMenuSeparator} from '@/shadcn_components/ui/dropdown-menu';
import {Check, FilterIcon, XIcon} from 'lucide-react';
import {Popover, PopoverContent, PopoverContentWithoutPortal} from '@/shadcn_components/ui/popover';
import {PopoverTrigger} from '@radix-ui/react-popover';
import {FormProvider, useForm} from 'react-hook-form';
import {BaseRecord, CrudFilters, LogicalFilter, useTableReturnType} from '@refinedev/core';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shadcn_components/ui/collapsible';
import {Separator} from '@/shadcn_components/ui/separator';
import {isShallowEqual} from '@/lib/utils';
import {cn} from '@/lib/tailwind-utils';

export interface DataTableFiltersProps<TData extends BaseRecord = BaseRecord> {
  children?: ReactElement;
  defaultValues?: TData;
  transformFilter?: (data: TData) => CrudFilters;
  className?: string;
  table: useTableReturnType<TData, any>;
  showAs?: 'popover' | 'collapsible';
  autoApply?: boolean;
  closeWhenApply?: boolean;
}

const DataTableFilters: React.FC<DataTableFiltersProps> = ({
  children,
  defaultValues,
  transformFilter = data =>
    Object.keys(data).map(key => {
      return {field: key, operator: 'eq', value: data[key]} as LogicalFilter;
    }),
  className,
  table,
  showAs = 'popover',
  autoApply = false,
  closeWhenApply = true,
}) => {
  const form = useForm({defaultValues});
  const [popoverOpen, setPopoverOpen] = React.useState(false);

  ///// for auto apply
  const {
    formState: {isValidating, isValid},
  } = form;
  const [submittedData, setSubmittedData] = React.useState(defaultValues);
  const data = form.watch();
  React.useEffect(() => {
    if (autoApply && isValid && !isValidating && !isShallowEqual(data, submittedData)) {
      // console.log(data, submittedData);
      setSubmittedData(data);
      handleApply(data);
    }
  }, [isValid, data, isValidating, autoApply, submittedData, handleApply, setSubmittedData]);

  // apply filter
  function handleApply(data) {
    const transformedData = transformFilter(data);
    // convert form data to tanstack table filters
    table.setFilters(transformedData, 'replace');
    if (closeWhenApply && !autoApply) setPopoverOpen(false);
  }

  function clearFilters() {
    form.reset();
    setSubmittedData({});
    // clear tanstack table filters
    // table.setColumnFilters([]);
    table.setFilters([], 'replace');
    console.log('clear filter');
    if (closeWhenApply && !autoApply) setPopoverOpen(false);
  }

  // apply table filters to form values
  useEffect(() => {
    for (const filter of table.filters) {
      const logicalFilter = filter as LogicalFilter;
      const field = logicalFilter.field;
      const value = logicalFilter.value;
      if (field) form.setValue(field, value);
    }
  }, [table.filters]);

  const TriggerButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
  >((props, ref) => {
    return (
      <Button variant="outline" size="sm" className="" {...props} ref={ref}>
        <FilterIcon />
        Lọc
      </Button>
    );
  });

  if (showAs === 'popover')
    return (
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen} modal>
        <PopoverTrigger asChild>
          <TriggerButton />
        </PopoverTrigger>
        {popoverOpen && (
          <PopoverContentWithoutPortal className={className}>
            <FormProvider {...form}>
              <DropdownMenuLabel>Lọc</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {children}
              <DropdownMenuSeparator />
              <div className="flex justify-center space-x-1">
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-24 h-8"
                  type="button"
                  onClick={clearFilters}>
                  Xóa bộ lọc
                </Button>
                {!autoApply && (
                  <Button
                    variant="default"
                    size="sm"
                    className="w-24 h-8"
                    type="button"
                    onClick={form.handleSubmit(handleApply)}>
                    Áp dụng
                  </Button>
                )}
              </div>
            </FormProvider>
          </PopoverContentWithoutPortal>
        )}
      </Popover>
    );

  // inline filter form
  return (
    <Collapsible
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      className="flex flex-row items-center space-x-1 transition-all duration-300">
      <CollapsibleTrigger asChild>
        <TriggerButton className={cn({'bg-secondary': popoverOpen})} />
      </CollapsibleTrigger>
      {popoverOpen && (
        <CollapsibleContent className="flex flex-row items-center space-x-1">
          <FormProvider {...form}>
            {children}
            <div className="flex justify-center space-x-1">
              {!autoApply && (
                <Button
                  variant="outline"
                  size="icon"
                  className="w-6 h-6 text-green-600"
                  type="button"
                  title="Áp dụng"
                  onClick={form.handleSubmit(handleApply)}>
                  <Check />
                </Button>
              )}
              <Button
                variant="outline"
                size="icon"
                className="w-6 h-6"
                type="button"
                title="Xóa bộ lọc"
                onClick={clearFilters}>
                <XIcon />
              </Button>
            </div>
          </FormProvider>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
};

export default DataTableFilters;
